import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/Button';
import { useSmsAuth } from '../hooks/useSmsAuth';
import { useAuth } from '../hooks/useAuth';

export const SmsVerifyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyLink, loading, error, storedPhone, clearError } = useSmsAuth();
  const { user, loading: authLoading } = useAuth();
  const [verificationAttempted, setVerificationAttempted] = useState<boolean>(false);

  useEffect(() => {
    const token = searchParams.get('token');

    if (user) {
      navigate('/dashboard');
      return;
    }

    if (authLoading) return;

    if (token && !verificationAttempted && !loading) {
      setVerificationAttempted(true);
      verifyLink(token).catch(() => {});
    }
  }, [searchParams, user, navigate, verifyLink, verificationAttempted, loading, authLoading]);

  // Navigate to dashboard once user is authenticated
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleRetry = (): void => {
    clearError();
    navigate('/login');
  };

  if (authLoading || loading) {
    return (
      <div className="page-shell mobile-safe-padding mobile-safe-top flex min-h-screen items-center justify-center">
        <LoadingSpinner message="Verifica del link di accesso in corso..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-shell mobile-safe-padding mobile-safe-top flex min-h-screen items-center justify-center">
        <div className="enpal-card w-full max-w-md text-center">
          <img src="/enpal-logo.png" alt="Enpal" className="mx-auto mb-6 h-10 w-auto" />
          <h1 className="mb-4 text-3xl font-bold text-enpal-downriver">Verifica fallita</h1>
          <p className="mb-4 text-base text-enpal-stone-grey">{error}</p>
          <Button onClick={handleRetry} fullWidth>
            Richiedi nuovo link
          </Button>
        </div>
      </div>
    );
  }

  // No token in URL
  const token = searchParams.get('token');
  if (!token) {
    return (
      <div className="page-shell mobile-safe-padding mobile-safe-top flex min-h-screen items-center justify-center">
        <div className="enpal-card w-full max-w-md text-center">
          <img src="/enpal-logo.png" alt="Enpal" className="mx-auto mb-6 h-10 w-auto" />
          <h1 className="mb-4 text-3xl font-bold text-enpal-downriver">Link non valido</h1>
          <p className="mb-3 text-base text-enpal-stone-grey">
            Il link di accesso non è valido o è incompleto.
          </p>
          <p className="mb-6 text-sm text-slate-500">
            Richiedi un nuovo link dalla pagina di login.
          </p>
          <Button onClick={() => navigate('/login')} fullWidth>
            Vai al login
          </Button>
        </div>
      </div>
    );
  }

  // Show waiting state while verification is pending
  if (storedPhone) {
    return (
      <div className="page-shell mobile-safe-padding mobile-safe-top flex min-h-screen items-center justify-center">
        <div className="enpal-card w-full max-w-md text-center">
          <img src="/enpal-logo.png" alt="Enpal" className="mx-auto mb-6 h-10 w-auto" />
          <h1 className="mb-4 text-3xl font-bold text-enpal-downriver">Verifica in corso</h1>
          <p className="mb-4 text-base text-enpal-stone-grey">
            Stiamo verificando il tuo link di accesso per il numero {storedPhone}
          </p>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell mobile-safe-padding mobile-safe-top flex min-h-screen items-center justify-center">
      <LoadingSpinner message="Verifica in corso..." />
    </div>
  );
};
